import { Provider, Type } from '@angular/core';
import { GridApi, RowClickedEvent } from 'ag-grid-community';
import { BehaviorSubject, defer, map, Observable, shareReplay } from 'rxjs';
import { ListViewDataBase } from './master-list-view.model';

export abstract class AbstractSimpleListViewService<Model = ListViewDataBase> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected constructor(protected readonly store?: any) {}
  abstract readonly data$: Observable<Model[]>;
  abstract readonly loading$: Observable<boolean>;

  readonly items$ = defer(() => this.data$).pipe(shareReplay({ bufferSize: 1, refCount: true }));
  readonly hasItems$ = defer(() => this.items$).pipe(map((data) => data.length > 0));

  private readonly activeItem$$ = new BehaviorSubject<Model | null>(null);
  readonly activeItem$ = this.activeItem$$.asObservable();

  private readonly gridApi$$ = new BehaviorSubject<GridApi | null>(null);
  readonly gridApi$ = this.gridApi$$.asObservable();

  get gridApi() {
    return this.gridApi$$.getValue();
  }

  getAll?(): Promise<void>;
  add?(): Promise<void>;
  edit?(rowClickedEvent: RowClickedEvent & { data: Model }): Promise<void>;
  delete?(): Promise<void>;
  getIdProperty?(): keyof Model;

  setActiveItem(item: Model | null) {
    this.activeItem$$.next(item);
  }

  /**
   * Sets the ag-grid `GridApi` object in the service.
   * Called by the `list-view-grid.component`.
   */
  setGridApi(gridApi: GridApi | null) {
    this.gridApi$$.next(gridApi);
  }
}

export abstract class AbstractListViewService<
  Model extends ListViewDataBase = ListViewDataBase,
> extends AbstractSimpleListViewService<Model> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(protected override readonly store?: any) {
    super(store);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function provideSimpleListView(listViewService: Type<AbstractSimpleListViewService<any>>): Provider[] {
  return [
    listViewService,
    {
      provide: AbstractListViewService,
      useExisting: listViewService,
    },
  ];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function provideListView(listViewService: Type<AbstractListViewService<any>>): Provider[] {
  return [
    listViewService,
    {
      provide: AbstractListViewService,
      useExisting: listViewService,
    },
  ];
}
